import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { useAuth } from '~/services/auth';
import type { useSendTvLink } from '~/pages/Onboarding/useSendTvLink';
import { consoleAddress } from '~/utils/consoleAddress';

import styles from './TvHelpCard.module.css';

/**
 * Télécommande sans écran en face : une ligne sous la mini-TV (« En attente de
 * l'écran ») qui dit où ouvrir Unboared + « Recevoir le lien » (connexion par email).
 * Disparaît seule dès que l'écran bat. V2 : plus de carte ni de pavé de texte.
 * `where="picker"` : en tête de « Choisir un jeu » : ouvrir la TV d'abord, puis choisir.
 * `link` : état « Recevoir le lien » tenu par la télécommande, partagé par les deux aides.
 * Mesure : `animator_tv_help_shown` (sous la mini-TV, comparable à avant) ou
 * `animator_tv_help_picker_shown` (à chaque ouverture de la liste) ; envois préfixés pareil.
 */
export function TvHelpCard({
    clientID,
    link,
    className,
    where = 'cockpit',
}: {
    clientID: string;
    link: ReturnType<typeof useSendTvLink>;
    className?: string;
    where?: 'cockpit' | 'picker';
}) {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const email = auth?.email || '';
    const { state, sendFrom, canSend } = link;
    const source = where === 'picker' ? 'animator_tv_help_picker' : 'animator_tv_help';

    useEffect(() => {
        logEvent(`${source}_shown`, clientID, { clientID });
    }, [clientID, source]);

    return (
        <p className={`${styles.line} ${className ?? ''}`}>
            {t(where === 'picker' ? 'animator.screenWait.picker' : 'animator.screenWait.text', {
                address: consoleAddress(),
            })}{' '}
            {state === 'sent' && (
                <span className={styles.sent} role="status">
                    {t('animator.screenWait.sent', { email })}
                </span>
            )}
            {state === 'error' && (
                <span className={styles.error} role="alert">
                    {t('animator.screenWait.error')}
                </span>
            )}
            {state !== 'sent' && canSend && (
                <button
                    type="button"
                    className={styles.link}
                    onClick={() => sendFrom(source)}
                    disabled={state === 'sending'}
                >
                    {state === 'sending' ? t('animator.screenWait.sending') : t('animator.screenWait.link')}
                </button>
            )}
        </p>
    );
}
